import { useState } from 'react';
import { Field } from '@/components/ui';
import { IconExternal, IconRefresh } from '@/components/Icons';
import { useConnectKirvano, useSyncKirvanoAccount, useUpdateKirvanoAccount } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { formatNumber } from '@/lib/format';
import type { KirvanoAccount } from '@/lib/types';

const NEW = '__nova-conta__';

const formatWhen = (iso: string) => new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

const groupPreview = (name: string) => (/^kirvano\b/i.test(name.trim()) ? name.trim() : `Kirvano · ${name.trim()}`);

/** Leva o navegador ao "entrar com a Kirvano"; a volta cai em Meus links. */
function useGoToKirvano() {
  const toast = useToast();
  const connect = useConnectKirvano();
  async function go(body: { name: string } | { accountId: string }) {
    try {
      const { authorizeUrl } = await connect.mutateAsync(body);
      window.location.assign(authorizeUrl);
    } catch (caught) {
      toast.fromError(caught);
    }
  }
  return { go, busy: connect.isPending };
}

interface PickerProps {
  accounts: KirvanoAccount[];
  value: string;
  onChange: (accountId: string) => void;
  error?: string;
}

/**
 * Qual conta da Kirvano — ou uma nova, conectada pelo login da própria Kirvano.
 * O nome digitado vira o grupo dos links ("Kirvano · Maria").
 */
export function KirvanoAccountPicker({ accounts, value, onChange, error }: PickerProps) {
  const { go, busy } = useGoToKirvano();
  const [creating, setCreating] = useState(accounts.length === 0);
  const [name, setName] = useState('');
  const canConnect = name.trim().length >= 2 && !busy;

  return (
    <>
      {accounts.length > 0 ? (
        <Field label="Conta da Kirvano *" error={creating ? undefined : error} hint="Cada conta conectada tem o seu grupo de links.">
          <select
            className="inp"
            value={creating ? NEW : value}
            onChange={(event) => {
              const next = event.target.value;
              setCreating(next === NEW);
              onChange(next === NEW ? '' : next);
            }}
          >
            <option value="" disabled>Escolha a conta</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.groupName}{!account.active ? ' (desligada)' : !account.connected ? ' (reconectar)' : ''}
              </option>
            ))}
            <option value={NEW}>+ Conectar outra conta…</option>
          </select>
        </Field>
      ) : null}

      {creating ? (
        <div className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Field
            label={accounts.length > 0 ? 'Nova conta' : 'Conecte a primeira conta da Kirvano'}
            error={accounts.length === 0 ? error : undefined}
            hint="De quem é a conta: o produtor, a loja… Vira o grupo dos links na página."
          >
            <input
              className="inp"
              value={name}
              maxLength={40}
              placeholder="Ex.: nome do produtor"
              autoFocus={accounts.length > 0}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                // Enter aqui conecta a conta, não envia o formulário.
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (canConnect) void go({ name: name.trim() });
                }
              }}
            />
          </Field>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={() => void go({ name: name.trim() })} disabled={!canConnect}>
              {busy ? <span className="spinner" /> : <IconExternal />} Entrar com a Kirvano
            </button>
            {name.trim().length >= 2 ? (
              <span className="muted" style={{ fontSize: 12 }}>
                Grupo: <strong>{groupPreview(name)}</strong>
              </span>
            ) : null}
          </div>
          <p className="muted" style={{ fontSize: 12, margin: 0, lineHeight: 1.5 }}>
            Abre o login da Kirvano. Entre com a conta dos produtos e autorize a leitura: o painel só lê produtos e vendas, não muda nada lá.
          </p>
        </div>
      ) : null}
    </>
  );
}

/** A conta escolhida: se está conectada, quantos produtos vieram, a última leitura, atualizar e desligar. */
export function KirvanoConnectionPanel({ account }: { account: KirvanoAccount }) {
  const toast = useToast();
  const { go, busy: reconnecting } = useGoToKirvano();
  const sync = useSyncKirvanoAccount();
  const update = useUpdateKirvanoAccount();
  const reading = account.connected && (account.syncing || !account.lastSyncAt);

  async function syncNow() {
    try {
      await sync.mutateAsync(account.id);
      toast.success('Atualização pedida. Com o limite por minuto da Kirvano, o catálogo inteiro pode levar alguns minutos.');
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  async function toggleActive() {
    try {
      await update.mutateAsync({ id: account.id, active: !account.active });
      toast.success(account.active ? 'Conta desligada: a leitura dela para.' : 'Conta religada.');
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <div className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13.5 }}>{account.groupName}</strong>
        {!account.active ? (
          <span className="chip chip-negative">Desligada</span>
        ) : !account.connected ? (
          <span className="chip chip-warning">Precisa reconectar</span>
        ) : reading ? (
          <span className="chip chip-warning" role="status"><span className="spinner" style={{ width: 10, height: 10 }} /> Lendo a Kirvano…</span>
        ) : (
          <span className="chip chip-positive">Conectada</span>
        )}
        {account.companyName ? <span className="muted truncate" style={{ fontSize: 12, maxWidth: 260 }}>{account.companyName}</span> : null}
      </div>

      {account.connected ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          <Stat label="Produtos no painel" value={`${formatNumber(account.links)} ${account.links === 1 ? 'link' : 'links'}`} />
          <Stat label="Vendas no ranking" value={formatNumber(account.credited)} />
          <Stat label="Última leitura" value={account.lastSyncAt ? formatWhen(account.lastSyncAt) : '—'} />
        </div>
      ) : null}

      {account.connected && account.links > 0 ? (
        <p className="muted" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>
          Os produtos chegam <strong>escondidos dos membros</strong>. Em Meus links, no grupo <strong>{account.groupName}</strong>, use
          {' '}<strong>Quem recebe</strong> em cada oferta para liberar. As vendas são lidas sozinhas a cada 5 minutos; os produtos, de 2 em 2 horas (ou em Atualizar agora).
        </p>
      ) : null}

      {account.lastSyncError && !reading ? (
        <p className="field-error" role="alert" style={{ margin: 0 }}>{account.lastSyncError}</p>
      ) : null}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', borderTop: '1px solid var(--line)', paddingTop: 8 }}>
        {account.connected ? (
          <button type="button" className="btn btn-ghost" onClick={() => void syncNow()} disabled={sync.isPending || reading || !account.active}>
            {sync.isPending ? <span className="spinner" /> : <IconRefresh />} Atualizar agora
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={() => void go({ accountId: account.id })} disabled={reconnecting}>
            {reconnecting ? <span className="spinner" /> : <IconExternal />} Entrar com a Kirvano
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" className="btn btn-ghost" onClick={() => void toggleActive()} disabled={update.isPending}>
          {account.active ? 'Desligar' : 'Religar'}
        </button>
      </div>

    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: 'var(--sunken)', borderRadius: 'var(--radius-sm)', padding: '7px 10px' }}>
      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
      <div className="num" style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
    </div>
  );
}
